import type { CSSProperties } from "react"

import { type LandingPageSettings, landingPageButtonStyles, landingPageFonts } from "@forento/core/models/landing"
import {
	type LandingPageBrandColor,
	landingPagePatterns,
	landingPageStyles,
	resolveLandingPageAccentColor,
} from "@forento/core/models/landingStyle"
import { isColorDark } from "@forento/core/utilities/color"

export type LandingThemeStyle = CSSProperties & {
	"--landing-primary": string
	"--landing-primary-foreground": string
	"--landing-background": string
	"--landing-surface": string
	"--landing-surface-muted": string
	"--landing-foreground": string
	"--landing-foreground-muted": string
	"--landing-foreground-subtle": string
	"--landing-border": string
	"--landing-header-background": string
	"--landing-header-foreground": string
	"--landing-footer-background": string
	"--landing-footer-foreground": string
	"--landing-footer-foreground-muted": string
	"--landing-pattern-image": string
	"--landing-pattern-size": string
	"--landing-font-heading": string
	"--landing-font-body": string
	"--landing-button-radius": string
	"--landing-button-background": string
	"--landing-button-foreground": string
	"--landing-button-shadow": string
	"--landing-button-hover-background": string
	"--landing-button-hover-foreground": string
	"--landing-button-hover-shadow": string
	"--landing-button-active-shadow": string
	"--landing-button-active-transform": string
}

export type LandingThemeSettings = Pick<LandingPageSettings, "styleId" | "usesBrandColor" | "fontId" | "buttonStyleId">

export function getLandingThemeStyle(
	brandColor: LandingPageBrandColor,
	settings: LandingThemeSettings,
): LandingThemeStyle {
	const color = resolveLandingPageAccentColor(brandColor, settings)
	const { palette, pattern } = landingPageStyles[settings.styleId]
	const font = landingPageFonts[settings.fontId]
	const buttonStyle = landingPageButtonStyles[settings.buttonStyleId]

	return {
		"--landing-primary": color,
		"--landing-primary-foreground": isColorDark(color) ? "#ffffff" : "#111827",
		"--landing-background": palette.background,
		"--landing-surface": palette.surface,
		"--landing-surface-muted": palette.surfaceMuted,
		"--landing-foreground": palette.foreground,
		"--landing-foreground-muted": palette.foregroundMuted,
		"--landing-foreground-subtle": palette.foregroundSubtle,
		"--landing-border": palette.border,
		"--landing-header-background": palette.headerBackground,
		"--landing-header-foreground": palette.headerForeground,
		"--landing-footer-background": palette.footerBackground,
		"--landing-footer-foreground": palette.footerForeground,
		"--landing-footer-foreground-muted": palette.footerForegroundMuted,
		"--landing-pattern-image": landingPagePatterns[pattern].image,
		"--landing-pattern-size": landingPagePatterns[pattern].size,
		"--landing-font-heading": font.headingFamily,
		"--landing-font-body": font.bodyFamily,
		"--landing-button-radius": buttonStyle.radius,
		"--landing-button-background": buttonStyle.background,
		"--landing-button-foreground": buttonStyle.foreground,
		"--landing-button-shadow": buttonStyle.shadow,
		"--landing-button-hover-background": buttonStyle.hoverBackground,
		"--landing-button-hover-foreground": buttonStyle.hoverForeground,
		"--landing-button-hover-shadow": buttonStyle.hoverShadow,
		"--landing-button-active-shadow": buttonStyle.activeShadow,
		"--landing-button-active-transform": buttonStyle.activeTransform,
	}
}
